import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getAuth } from 'firebase/auth'
import { FiArrowLeft, FiCreditCard, FiLock } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { useUserRealtimeData } from '../hooks/useUserRealtimeData'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import { apiUrl } from '../config/api'
import { buildPaymentMethodFromPlan } from '../utils/billingType'
import { getProfileDisplayName, getProfilePhone } from '../utils/userProfile'
import { normalizePlanId, planDisplayName } from '../utils/subscriptionPlan'

const FIELD = 'input-field !min-h-[42px] !py-2.5 !px-4 !text-sm'

const UpdateCardPage = () => {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { profile, plan } = useUserRealtimeData()

  const [loading, setLoading] = useState(false)
  const [cepLoading, setCepLoading] = useState(false)

  const [addressData, setAddressData] = useState({
    name: '',
    phone: '',
    postalCode: '',
    address: '',
    addressNumber: '',
    complement: '',
    province: '',
    city: '',
    state: '',
  })

  const [cardData, setCardData] = useState({
    email: '',
    cpf: '',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
  })

  const paymentMethod = buildPaymentMethodFromPlan(plan)
  const canUpdateCard = Boolean(paymentMethod?.canUpdateCard)
  const isCancelled = Boolean(plan?.cancelledAt)
  const planLabel = plan?.planName || planDisplayName(plan?.planId)

  const cepNumber = useMemo(() => addressData.postalCode.replace(/\D/g, ''), [addressData.postalCode])

  useEffect(() => {
    if (!user) return
    setAddressData((prev) => ({
      ...prev,
      name: prev.name || getProfileDisplayName(profile, user.displayName),
      phone: prev.phone || getProfilePhone(profile),
    }))
    setCardData((prev) => ({
      ...prev,
      email: prev.email || user.email || '',
    }))
  }, [user, profile])

  if (authLoading) return null
  if (!user) return <Navigate to="/" replace />

  if (!canUpdateCard) {
    return (
      <main className="w-full min-w-0 pt-24 sm:pt-28 pb-16">
        <div className="container-custom max-w-lg">
          <Link
            to="/account"
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-accent mb-6"
          >
            <FiArrowLeft className="w-4 h-4" />
            Voltar para Minha conta
          </Link>
          <Card padding="lg" hover={false}>
            <h1 className="text-xl font-extrabold text-gray-900 dark:text-white mb-3">
              Troca de cartão indisponível
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {isCancelled ? (
                <>
                  Sua renovação está cancelada. Para cadastrar um novo cartão, use{' '}
                  <strong>Minha conta → Ir para o checkout e reativar</strong>.
                </>
              ) : normalizePlanId(plan?.planId) === 'gratuito' ? (
                'Você precisa de uma assinatura ativa no cartão de crédito para trocar o cartão.'
              ) : (
                'A troca de cartão está disponível apenas para assinaturas ativas com cobrança no cartão de crédito.'
              )}
            </p>
            <Button
              type="button"
              variant="primary"
              size="md"
              className="mt-5"
              onClick={() => navigate(isCancelled ? `/checkout/${normalizePlanId(plan?.planId) || 'basico'}` : '/account')}
            >
              {isCancelled ? 'Ir para o checkout' : 'Voltar para Minha conta'}
            </Button>
          </Card>
        </div>
      </main>
    )
  }

  const validate = () => {
    if (!cardData.cardNumber || !cardData.cardName || !cardData.expiryDate || !cardData.cvv) {
      return 'Preencha todos os dados do cartão.'
    }
    if (!cardData.cpf.replace(/\D/g, '')) return 'Informe o CPF do titular.'
    if (!addressData.name || !addressData.phone) return 'Preencha nome e telefone.'
    if (cepNumber.length !== 8) return 'CEP inválido.'
    if (!addressData.address || !addressData.city || !addressData.state || !addressData.province) {
      return 'Preencha todos os campos de endereço.'
    }
    if (!addressData.addressNumber) return 'Informe o número do endereço.'
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const err = validate()
    if (err) {
      toast.error(err)
      return
    }

    setLoading(true)
    try {
      const token = await getAuth().currentUser?.getIdToken()
      if (!token) throw new Error('Você precisa estar logado.')

      const resp = await fetch(apiUrl('/api/asaas/update-card'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          cardData: {
            cardNumber: cardData.cardNumber,
            cardName: cardData.cardName,
            expiryDate: cardData.expiryDate,
            cvv: cardData.cvv,
          },
          addressData: {
            ...addressData,
            email: cardData.email,
            cpfCnpj: cardData.cpf.replace(/\D/g, ''),
          },
        }),
      })
      const raw = await resp.text()
      const data = (() => {
        try { return JSON.parse(raw) } catch { return null }
      })()
      if (!data) {
        throw new Error(`Resposta inválida do backend. Início: ${raw.slice(0, 60)}`)
      }
      if (!resp.ok || !data.ok) throw new Error(data.error || 'Erro ao atualizar cartão.')

      toast.success('Cartão atualizado! As próximas cobranças usarão o novo cartão.')
      navigate('/account')
    } catch (err) {
      console.error(err)
      toast.error(err.message || 'Erro ao atualizar cartão.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="w-full min-w-0 pt-24 sm:pt-28 pb-16">
      <div className="container-custom">
        <Link
          to="/account"
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-accent mb-6"
        >
          <FiArrowLeft className="w-4 h-4" />
          Voltar para Minha conta
        </Link>

        <div className="mb-8">
          <p className="text-xs font-bold text-accent uppercase tracking-[0.2em] mb-2">Pagamento</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
            Trocar cartão de crédito
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-2 max-w-2xl leading-relaxed">
            Plano <strong className="text-gray-900 dark:text-white">{planLabel}</strong>. O novo cartão será usado nas
            próximas cobranças — não há cobrança imediata ao salvar.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2">
            <Card padding="lg" hover={false}>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-accent/10 dark:bg-accent/20 text-accent">
                    <FiCreditCard className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-extrabold text-gray-900 dark:text-white">Dados do novo cartão</h2>
                </div>

                <div>
                  <label className="form-label">Número do cartão</label>
                  <input
                    type="text"
                    required
                    value={cardData.cardNumber}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '')
                      value = value.replace(/(\d{4})(?=\d)/g, '$1 ')
                      setCardData({ ...cardData, cardNumber: value })
                    }}
                    maxLength={19}
                    className={FIELD}
                    placeholder="0000 0000 0000 0000"
                    autoComplete="cc-number"
                  />
                </div>

                <div>
                  <label className="form-label">Nome no cartão</label>
                  <input
                    type="text"
                    required
                    value={cardData.cardName}
                    onChange={(e) => setCardData({ ...cardData, cardName: e.target.value.toUpperCase() })}
                    className={FIELD}
                    placeholder="NOME COMPLETO"
                    autoComplete="cc-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Validade</label>
                    <input
                      type="text"
                      required
                      value={cardData.expiryDate}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '')
                        value = value.replace(/(\d{2})(?=\d)/g, '$1/')
                        setCardData({ ...cardData, expiryDate: value.slice(0, 5) })
                      }}
                      maxLength={5}
                      className={FIELD}
                      placeholder="MM/AA"
                      autoComplete="cc-exp"
                    />
                  </div>
                  <div>
                    <label className="form-label">CVV</label>
                    <input
                      type="text"
                      required
                      value={cardData.cvv}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                        setCardData({ ...cardData, cvv: value })
                      }}
                      maxLength={4}
                      className={FIELD}
                      placeholder="000"
                      autoComplete="cc-csc"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">CPF do titular</label>
                  <input
                    type="text"
                    required
                    value={cardData.cpf}
                    onChange={(e) => setCardData({ ...cardData, cpf: e.target.value })}
                    className={FIELD}
                    placeholder="000.000.000-00"
                  />
                </div>

                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-extrabold text-gray-900 dark:text-white mb-4">Dados de cobrança</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Nome completo</label>
                        <input
                          type="text"
                          required
                          value={addressData.name}
                          onChange={(e) => setAddressData({ ...addressData, name: e.target.value })}
                          className={FIELD}
                          autoComplete="name"
                        />
                      </div>
                      <div>
                        <label className="form-label">Telefone</label>
                        <input
                          type="tel"
                          required
                          value={addressData.phone}
                          onChange={(e) => setAddressData({ ...addressData, phone: e.target.value })}
                          className={FIELD}
                          autoComplete="tel"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label">CEP</label>
                      <input
                        type="text"
                        required
                        value={addressData.postalCode}
                        onChange={(e) => setAddressData({ ...addressData, postalCode: e.target.value })}
                        onBlur={async () => {
                          if (cepNumber.length !== 8) return
                          setCepLoading(true)
                          try {
                            const res = await fetch(`https://viacep.com.br/ws/${cepNumber}/json/`)
                            const data = await res.json()
                            if (data.erro) return
                            setAddressData((prev) => ({
                              ...prev,
                              address: data.logradouro || prev.address,
                              province: data.bairro || prev.province,
                              city: data.localidade || prev.city,
                              state: data.uf || prev.state,
                            }))
                          } catch {
                            // silencioso
                          } finally {
                            setCepLoading(false)
                          }
                        }}
                        className={FIELD}
                        placeholder="00000-000"
                      />
                      {cepLoading && <p className="text-xs text-gray-500 mt-1">Buscando CEP...</p>}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="sm:col-span-2">
                        <label className="form-label">Endereço</label>
                        <input
                          type="text"
                          required
                          value={addressData.address}
                          onChange={(e) => setAddressData({ ...addressData, address: e.target.value })}
                          className={FIELD}
                        />
                      </div>
                      <div>
                        <label className="form-label">Número</label>
                        <input
                          type="text"
                          required
                          value={addressData.addressNumber}
                          onChange={(e) => setAddressData({ ...addressData, addressNumber: e.target.value })}
                          className={FIELD}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="form-label">Bairro</label>
                        <input
                          type="text"
                          required
                          value={addressData.province}
                          onChange={(e) => setAddressData({ ...addressData, province: e.target.value })}
                          className={FIELD}
                        />
                      </div>
                      <div>
                        <label className="form-label">Cidade</label>
                        <input
                          type="text"
                          required
                          value={addressData.city}
                          onChange={(e) => setAddressData({ ...addressData, city: e.target.value })}
                          className={FIELD}
                        />
                      </div>
                      <div>
                        <label className="form-label">UF</label>
                        <input
                          type="text"
                          required
                          maxLength={2}
                          value={addressData.state}
                          onChange={(e) => setAddressData({ ...addressData, state: e.target.value.toUpperCase() })}
                          className={FIELD}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                  <FiLock className="w-5 h-5 text-accent flex-shrink-0" />
                  <span>Pagamento seguro processado pela central de pagamentos.</span>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    className="w-full sm:w-auto"
                    disabled={loading}
                    onClick={() => navigate('/account')}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary" size="md" className="w-full sm:flex-1" disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar novo cartão'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card padding="lg" hover={false} className="lg:sticky lg:top-28">
              <h2 className="text-sm font-extrabold text-gray-900 dark:text-white mb-3">Resumo</h2>
              <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                <li>• Assinatura ativa no plano {planLabel}</li>
                <li>• O cartão atual será substituído nas próximas cobranças</li>
                <li>• Nenhuma cobrança é feita ao salvar</li>
                <li>• Seu plano e valor mensal permanecem os mesmos</li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </main>
  )
}

export default UpdateCardPage
